import { Injectable } from '@angular/core';
import { WS_API_URL } from '../../../../../services/config';
import { IMessage, RxStomp, RxStompState } from '@stomp/rx-stomp';
import { EMPTY, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { STOMP_DEFAULT_CONFIG, TOPICS_URLS, TopicsEnum } from './constants';
import { TopicStoreType } from './models';

@Injectable({
  providedIn: 'root',
})
export class ServerMessagesService extends RxStomp {
  private readonly topicUrls = new Map<TopicsEnum, string>();
  private readonly topicStore = new Map<TopicsEnum, TopicStoreType>();

  constructor() {
    super();
  }

  setUpConnection(): void {
    if (this.active) {
      this.unsubscribeAll(false);
      void this.deactivate();
    }

    this.configure({
      ...STOMP_DEFAULT_CONFIG,
      brokerURL: WS_API_URL,
    });

    this.activate();
    this.subscribeAll();
  }

  disconnect(): void {
    if (this.active) {
      this.unsubscribeAll(true);
      void this.deactivate();
    }
  }

  subscribeToTopic<TMessage>(topic: TopicsEnum, params?: object): Observable<TMessage> {
    let url = TOPICS_URLS[topic];

    if (this.topicStore.has(topic)) {
      this.unsubscribe(topic, true);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]: [string, unknown]) => {
        url = url.replace(`:${key}`, String(value));
      });
    }

    if (url.includes(':')) {
      throw new Error(`Missing WebSocket topic parameter for ${url}`);
    }

    this.topicUrls.set(topic, url);
    this.addTopicToStore<TMessage>(topic, url);

    return this.connectionState$.pipe(
      filter((state: RxStompState) => state === RxStompState.OPEN),
      distinctUntilChanged(),
      switchMap(() => {
        const storedTopic = this.topicStore.get(topic);

        if (!storedTopic) {
          return EMPTY;
        }

        return storedTopic.topic$ as Observable<TMessage>;
      }),
    );
  }

  unsubscribe(topic: TopicsEnum, removeUrl: boolean = true): void {
    const topicStoreItem = this.topicStore.get(topic);

    topicStoreItem?.unsubscribe$.next(true);
    topicStoreItem?.unsubscribe$.complete();

    this.topicStore.delete(topic);

    if (removeUrl) {
      this.topicUrls.delete(topic);
    }
  }

  private addTopicToStore<TMessage>(topic: TopicsEnum, url: string): void {
    const unsubscribe$ = new Subject<boolean>();

    this.topicStore.set(topic, {
      topic$: this.subscribe<TMessage>(url).pipe(takeUntil(unsubscribe$.pipe(filter(Boolean)))),
      unsubscribe$,
    });
  }

  private subscribeAll(): void {
    this.topicUrls.forEach((url: string, topic: TopicsEnum) => {
      this.addTopicToStore(topic, url);
    });
  }

  private unsubscribeAll(removeUrls: boolean = false): void {
    [...this.topicStore.keys()].forEach((topic: TopicsEnum) => {
      this.unsubscribe(topic, removeUrls);
    });
  }

  private subscribe<T>(destination: string): Observable<T> {
    return this.watch({ destination }).pipe(
      filter((message: IMessage) => !!message?.body),
      map((message: IMessage) => JSON.parse(message.body)),
    );
  }
}
